// @ts-check
// Ouvidoria (/service/ombudsman; contrato 10 de atendimento, seções 15 e 24.3): só para os membros da fila (nem o
// Administrador vê sem entrar nela). Lista por situação e tipo, "Registrar manifestação" (por telefone ou presencial, com o
// código de acompanhamento mostrado uma vez), o atendimento aberto com a identidade conforme o modo, as respostas
// públicas e as consultas internas sem o pedinte, e "Exportar relatório anual" (sem nome nem contato).
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { searchParamsState, updateSearchParams } from '../../app/router.js';
import { getServiceMe, listInbox, listServiceTeams, ombudsmanReportUrl, registerManifestation } from '../../data/service-desk.js';
import { requestFile } from '../../data/api-client.js';
import { downloadBlob } from '../../lib/csv.js';
import { formatShortDate } from '../../lib/format.js';
import { IDENTITY_MODES, MANIFESTATION_TYPES } from '../../../../shared/service-desk/ticket-values.js';
import { PageCard, PageHeader } from '../page/page-card.js';
import { Button } from '../../ui/button.js';
import { EmptyState, Skeleton } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { Modal } from '../../ui/modal.js';
import { useIsMobile } from '../../ui/hooks.js';
import { showSuccessSnackbar } from '../../ui/snackbar.js';
import { ServiceTicketPane } from './service-ticket-pane.js';
import { DialogError, SelectField, TextAreaField, TextField, runDialogAction } from './service-dialogs.js';
import { SlaChip, TicketValueTag, showServiceError, ticketValueLabel } from './service-display.js';
import { t } from '../../i18n/index.js';

/** Situações da lista da Ouvidoria. */
const OMBUDSMAN_STATUS_FILTERS = Object.freeze(['open', 'closed']);

/** @param {{ onClose: () => void, onCreated: (result: { ticket: any, accessCode: string }) => void }} props */
function RegisterManifestationDialog({ onClose, onCreated }) {
  const [manifestationType, setManifestationType] = useState('COMPLAINT');
  const [identityMode, setIdentityMode] = useState('IDENTIFIED');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const isAnonymous = identityMode === 'ANONYMOUS';
  const isReady = subject.trim() && body.trim() && (isAnonymous || name.trim());
  return html`<${Modal}
    title=${t('ombudsman.register.title')}
    subtitle=${t('ombudsman.register.subtitle')}
    confirmLabel=${t('ombudsman.register.confirm')}
    isConfirmDisabled=${!isReady}
    isLoading=${isSaving}
    onClose=${onClose}
    onConfirm=${() => runDialogAction(async () => {
      const result = await registerManifestation({
        manifestationType, identityMode, subject: subject.trim(), body: body.trim(),
        ...(isAnonymous ? {} : { contact: { name: name.trim(), email: email.trim() || null, phone: phone.trim() || null } }),
      });
      onCreated(result);
    }, setIsSaving, setError)}
  >
    <div class="service-dialog-body" data-testid="ombudsman-register-dialog">
      <div class="form-field-row">
        <${SelectField} label=${t('ombudsman.fields.type')} value=${manifestationType} onChange=${setManifestationType}
          options=${MANIFESTATION_TYPES.map((value) => ({ value, label: ticketValueLabel('manifestationType', value) }))} />
        <${SelectField} label=${t('ombudsman.fields.identityMode')} value=${identityMode} onChange=${setIdentityMode}
          options=${IDENTITY_MODES.map((value) => ({ value, label: ticketValueLabel('identityMode', value) }))} />
      </div>
      <p class="service-dialog-note"><${Icon} name="IconInfoCircle" size=${14} />${t(`ombudsman.identityHints.${identityMode}`)}</p>
      <${TextField} label=${t('ombudsman.fields.subject')} value=${subject} onChange=${setSubject} />
      <${TextAreaField} label=${t('ombudsman.fields.body')} value=${body} onChange=${setBody} rows=${5} />
      ${!isAnonymous
        ? html`<${TextField} label=${t('ombudsman.fields.name')} value=${name} onChange=${setName} maxLength=${120} />
          <div class="form-field-row">
            <${TextField} label=${t('ombudsman.fields.email')} value=${email} onChange=${setEmail} type="email" maxLength=${160} />
            <${TextField} label=${t('ombudsman.fields.phone')} value=${phone} onChange=${setPhone} type="tel" maxLength=${30} />
          </div>`
        : null}
      <${DialogError} message=${error} />
    </div>
  <//>`;
}

/** @param {{ isOpen: boolean }} props */
function NotMember({ isOpen }) {
  if (!isOpen) return null;
  return html`<div class="service-empty" data-testid="ombudsman-not-member">
    <${EmptyState} icon="IconLock" title=${t('ombudsman.notMemberTitle')} subtitle=${t('ombudsman.notMember')} />
  </div>`;
}

export function OmbudsmanPage() {
  const [me, setMe] = useState(/** @type {any} */ (null));
  const [teams, setTeams] = useState(/** @type {any[]} */ ([]));
  const [status, setStatus] = useState(/** @type {'loading' | 'ready' | 'error'} */ ('loading'));
  const [items, setItems] = useState(/** @type {any[] | null} */ (null));
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);
  const [createdCode, setCreatedCode] = useState(/** @type {{ protocol: string, accessCode: string } | null} */ (null));
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const isMobile = useIsMobile();
  const statusFilter = searchParamsState.value.get('status') === 'closed' ? 'closed' : 'open';
  const typeFilter = searchParamsState.value.get('type');
  const selectedId = searchParamsState.value.get('ticket');
  const ombudsmanTeam = (me?.teams ?? []).find((team) => team.teamKind === 'OMBUDSMAN') ?? null;

  useEffect(() => {
    document.title = t('serviceDesk.screens.ombudsman');
    Promise.all([getServiceMe(), listServiceTeams()])
      .then(([loadedMe, loadedTeams]) => {
        setMe(loadedMe);
        setTeams(loadedTeams);
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
  }, []);

  const loadList = async () => {
    if (!ombudsmanTeam) return;
    try {
      const response = await listInbox({ view: statusFilter === 'closed' ? 'closed' : 'team', teamId: ombudsmanTeam.id, limit: '50' });
      setItems(response.items.filter((item) => item.ticket));
    } catch (error) {
      showServiceError(error);
      setItems([]);
    }
  };
  useEffect(() => {
    setItems(null);
    loadList();
  }, [ombudsmanTeam?.id, statusFilter]);

  const exportReport = async () => {
    try {
      const { blob, fileName } = await requestFile(ombudsmanReportUrl(reportYear));
      downloadBlob(blob, fileName ?? `ouvidoria-${reportYear}.csv`);
    } catch (error) {
      showServiceError(error);
    }
  };

  const visibleItems = (items ?? []).filter((item) => !typeFilter || item.ticket.manifestation?.type === typeFilter);
  const header = html`<${PageHeader} icon="IconSpeakerphone" title=${t('serviceDesk.screens.ombudsman')}
    actions=${ombudsmanTeam ? html`<div class="service-header-actions">
      <select class="text-input service-year-select" value=${String(reportYear)} aria-label=${t('ombudsman.reportYear')}
        onChange=${(/** @type {Event} */ event) => setReportYear(Number(/** @type {HTMLSelectElement} */ (event.currentTarget).value))}>
        ${[0, 1, 2].map((offset) => new Date().getFullYear() - offset).map((year) => html`<option key=${year} value=${String(year)}>${year}</option>`)}
      </select>
      <${Button} title=${isMobile ? undefined : t('ombudsman.exportReport')} ariaLabel=${t('ombudsman.exportReport')} icon="IconDownload" size="small" variant="secondary" onClick=${exportReport} />
      <${Button} title=${isMobile ? undefined : t('ombudsman.register.open')} ariaLabel=${t('ombudsman.register.open')} icon="IconPlus" size="small" accent="blue"
        onClick=${() => setIsRegisterOpen(true)} data-testid="ombudsman-register" />
    </div>` : null} />`;

  if (status === 'loading') return html`<${PageCard} className="service-screen" header=${header}><div class="service-inbox-loading"><${Skeleton} height=${120} /></div><//>`;
  if (status === 'error') {
    return html`<${PageCard} className="service-screen" header=${header}><div class="service-empty"><${EmptyState} icon="IconAlertTriangle" title=${t('serviceDesk.list.errorTitle')} /></div><//>`;
  }
  const showList = !isMobile || !selectedId;
  return html`<${PageCard} className="service-screen service-ombudsman-card" header=${header}>
    <${NotMember} isOpen=${!ombudsmanTeam} />
    ${ombudsmanTeam
      ? html`<div class="service-ombudsman" data-testid="ombudsman-page">
          ${showList
            ? html`<section class="service-consultations-list">
                <div class="service-ombudsman-filters">
                  ${OMBUDSMAN_STATUS_FILTERS.map((value) => html`<button key=${value} type="button" class="service-filter-button" data-active=${statusFilter === value ? 'true' : undefined}
                    onClick=${() => updateSearchParams({ status: value === 'closed' ? 'closed' : null, ticket: null })}>${t(`ombudsman.statusFilters.${value}`)}</button>`)}
                  <select class="text-input" value=${typeFilter ?? ''} aria-label=${t('ombudsman.fields.type')}
                    onChange=${(/** @type {Event} */ event) => updateSearchParams({ type: /** @type {HTMLSelectElement} */ (event.currentTarget).value || null })}>
                    <option value="">${t('ombudsman.allTypes')}</option>
                    ${MANIFESTATION_TYPES.map((value) => html`<option key=${value} value=${value}>${ticketValueLabel('manifestationType', value)}</option>`)}
                  </select>
                </div>
                ${items === null
                  ? html`<div class="service-inbox-skeleton">${[0, 1, 2].map((index) => html`<${Skeleton} key=${index} height=${56} />`)}</div>`
                  : visibleItems.length === 0
                    ? html`<div class="service-empty"><${EmptyState} icon="IconSpeakerphone" title=${t(`ombudsman.empty.${statusFilter}`)} /></div>`
                    : html`<ul class="service-inbox-ul">${visibleItems.map((item) => html`<li key=${item.ticket.id}>
                        <button type="button" class="service-inbox-row" data-selected=${item.ticket.id === selectedId ? 'true' : undefined}
                          onClick=${() => updateSearchParams({ ticket: item.ticket.id })}>
                          <span class="service-inbox-row-texts">
                            <span class="service-inbox-row-top"><span class="service-protocol">${item.ticket.protocol}</span>
                              <${TicketValueTag} field="manifestationType" value=${item.ticket.manifestation?.type} />
                              <span class="service-inbox-row-time">${formatShortDate(item.ticket.createdAt)}</span></span>
                            <span class="service-inbox-row-subject"><span class="service-inbox-row-subject-text">${item.ticket.subject}</span></span>
                            <span class="service-inbox-row-meta">
                              <${TicketValueTag} field="identityMode" value=${item.ticket.manifestation?.identityMode} icon="IconLock" />
                              <${TicketValueTag} field="status" value=${item.ticket.status} />
                              <${SlaChip} ticket=${item.ticket} compact=${true} />
                            </span>
                          </span>
                        </button>
                      </li>`)}</ul>`}
              </section>`
            : null}
          ${selectedId
            ? html`<${ServiceTicketPane} key=${selectedId} ticketId=${selectedId} threadId=${null} me=${me} teams=${teams} isWhoIsOpen=${false} showWhoIsToggle=${false}
                onToggleWhoIs=${() => {}} onBack=${isMobile ? () => updateSearchParams({ ticket: null }) : null} onChanged=${loadList} commandRequest=${null} />`
            : !isMobile
              ? html`<div class="service-pane service-pane-empty"><${EmptyState} icon="IconSpeakerphone" title=${t('ombudsman.noSelectionTitle')} subtitle=${t('ombudsman.noSelection')} /></div>`
              : null}
        </div>`
      : null}
    ${isRegisterOpen ? html`<${RegisterManifestationDialog} onClose=${() => setIsRegisterOpen(false)} onCreated=${(result) => {
      setIsRegisterOpen(false);
      setCreatedCode({ protocol: result?.ticket?.protocol ?? '', accessCode: result?.accessCode ?? '' });
      loadList();
      if (result?.ticket?.id) updateSearchParams({ ticket: result.ticket.id, status: null });
    }} />` : null}
    ${createdCode ? html`<${Modal} title=${t('ombudsman.register.doneTitle')} subtitle=${t('ombudsman.register.doneSubtitle')} confirmLabel=${t('ombudsman.register.understood')} hideCancel=${true}
      onClose=${() => setCreatedCode(null)} onConfirm=${() => {
        setCreatedCode(null);
        showSuccessSnackbar(t('ombudsman.register.done'));
      }}>
      <dl class="service-panel-facts" data-testid="ombudsman-access-code">
        <div><dt>${t('ombudsman.register.protocol')}</dt><dd class="service-code">${createdCode.protocol}</dd></div>
        <div><dt>${t('ombudsman.register.accessCode')}</dt><dd class="service-code">${createdCode.accessCode}</dd></div>
      </dl>
    <//>` : null}
  <//>`;
}

